import { useState, useEffect, useRef, useCallback } from 'react'
import {
  Stack,
  Group,
  Switch,
  NumberInput,
  Select,
  Paper,
  Text,
  ActionIcon,
  Badge,
  Loader,
  ScrollArea,
  Code,
  Box,
  Tooltip,
  CopyButton,
  Button,
} from '@mantine/core'
import {
  IconRefresh,
  IconPlayerPause,
  IconPlayerPlay,
  IconDownload,
  IconCopy,
  IconCheck,
  IconSearch,
  IconClearAll,
} from '@tabler/icons-react'

interface LogEntry {
  timestamp?: string
  taskId?: string
  message: string
  level?: 'info' | 'warn' | 'error' | 'debug'
}

interface ServiceLogsProps {
  serviceId: string
  serviceName: string
}

export function ServiceLogs({ serviceId, serviceName }: ServiceLogsProps) {
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [isFollowing, setIsFollowing] = useState(true)
  const [showTimestamps, setShowTimestamps] = useState(true)
  const [tailLines, setTailLines] = useState<number>(100)
  const [since, setSince] = useState<string>('1h')
  const [isLoading, setIsLoading] = useState(true)
  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState('')

  const scrollRef = useRef<HTMLDivElement>(null)
  const eventSourceRef = useRef<EventSource | null>(null)

  // Parse SSE log data
  const parseLogEntry = (data: string): LogEntry => {
    try {
      // Try to parse as JSON first
      const parsed = JSON.parse(data)
      return {
        timestamp: parsed.timestamp || parsed.time,
        taskId: parsed.task_id || parsed.taskId || parsed.container,
        message: parsed.message || parsed.log || parsed.msg || data,
        level: detectLogLevel(parsed.level || parsed.message || data),
      }
    } catch {
      // Plain text log
      // Check for common log formats: [timestamp] [level] message
      const timestampMatch = data.match(/^\[?(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}[^\]]*)\]?\s*/)
      const levelMatch = data.match(/\[(INFO|WARN|ERROR|DEBUG)\]/i)

      return {
        timestamp: timestampMatch?.[1],
        message: data.replace(timestampMatch?.[0] || '', '').replace(levelMatch?.[0] || '', '').trim(),
        level: detectLogLevel(levelMatch?.[1] || data),
      }
    }
  }

  const detectLogLevel = (text: string): 'info' | 'warn' | 'error' | 'debug' => {
    const lower = text.toLowerCase()
    if (lower.includes('error') || lower.includes('err') || lower.includes('fatal')) return 'error'
    if (lower.includes('warn') || lower.includes('warning')) return 'warn'
    if (lower.includes('debug') || lower.includes('trace')) return 'debug'
    return 'info'
  }

  const getLevelColor = (level?: string): string => {
    switch (level) {
      case 'error': return 'red'
      case 'warn': return 'yellow'
      case 'debug': return 'gray'
      default: return 'blue'
    }
  }

  // Calculate 'since' timestamp
  const getSinceTimestamp = useCallback((): string => {
    const now = Math.floor(Date.now() / 1000)
    switch (since) {
      case '15m': return String(now - 15 * 60)
      case '30m': return String(now - 30 * 60)
      case '1h': return String(now - 60 * 60)
      case '3h': return String(now - 3 * 60 * 60)
      case '6h': return String(now - 6 * 60 * 60)
      case '12h': return String(now - 12 * 60 * 60)
      case '24h': return String(now - 24 * 60 * 60)
      case '7d': return String(now - 7 * 24 * 60 * 60)
      default: return String(now - 60 * 60)
    }
  }, [since])

  // Connect to logs SSE endpoint
  const connectToLogs = useCallback(() => {
    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    setIsLoading(true)
    setError(null)

    const params = new URLSearchParams({
      tail: String(tailLines),
      timestamps: String(showTimestamps),
      follow: String(isFollowing),
      since: getSinceTimestamp(),
    })

    const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:2377'
    const url = `${apiBase}/v1.44/services/${serviceId}/logs?${params}`

    const eventSource = new EventSource(url)
    eventSourceRef.current = eventSource

    eventSource.onopen = () => {
      setIsConnected(true)
      setIsLoading(false)
    }

    eventSource.onmessage = (event) => {
      const entry = parseLogEntry(event.data)
      setLogs(prev => {
        const newLogs = [...prev, entry]
        // Keep only last 5000 logs to prevent memory issues
        return newLogs.slice(-5000)
      })
    }

    eventSource.onerror = (err) => {
      console.error('SSE error:', err)
      setIsConnected(false)
      setIsLoading(false)

      // Check if it's a network error vs intentional close
      if (eventSource.readyState === EventSource.CLOSED) {
        setError('Connection closed. Click refresh to reconnect.')
      } else {
        setError('Failed to connect to logs stream')
      }
    }

    return eventSource
  }, [serviceId, tailLines, showTimestamps, isFollowing, getSinceTimestamp])

  // Auto-scroll to bottom when following
  useEffect(() => {
    if (isFollowing && scrollRef.current) {
      const scrollArea = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]')
      if (scrollArea) {
        scrollArea.scrollTop = scrollArea.scrollHeight
      }
    }
  }, [logs, isFollowing])

  // Connect on mount and when params change
  useEffect(() => {
    const eventSource = connectToLogs()

    return () => {
      eventSource.close()
    }
  }, [connectToLogs])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }
    }
  }, [])

  // Filter logs
  const filteredLogs = filter
    ? logs.filter(log =>
        log.message.toLowerCase().includes(filter.toLowerCase()) ||
        log.taskId?.toLowerCase().includes(filter.toLowerCase())
      )
    : logs

  // Download logs as file
  const downloadLogs = () => {
    const content = filteredLogs
      .map(log => {
        let line = ''
        if (showTimestamps && log.timestamp) {
          line += `[${log.timestamp}] `
        }
        if (log.taskId) {
          line += `[${log.taskId}] `
        }
        line += log.message
        return line
      })
      .join('\n')

    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${serviceName}-logs-${new Date().toISOString().slice(0, 10)}.log`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Clear logs
  const clearLogs = () => {
    setLogs([])
  }

  return (
    <Stack gap="md">
      {/* Controls */}
      <Paper p="md" withBorder>
        <Group justify="space-between" wrap="wrap">
          <Group gap="md">
            <Switch
              label="Follow"
              checked={isFollowing}
              onChange={(e) => setIsFollowing(e.currentTarget.checked)}
              size="sm"
              thumbIcon={
                isFollowing
                  ? <IconPlayerPlay size={12} />
                  : <IconPlayerPause size={12} />
              }
            />
            <Switch
              label="Timestamps"
              checked={showTimestamps}
              onChange={(e) => setShowTimestamps(e.currentTarget.checked)}
              size="sm"
            />
          </Group>

          <Group gap="sm">
            <NumberInput
              label="Lines"
              value={tailLines}
              onChange={(val) => setTailLines(Number(val) || 100)}
              min={10}
              max={5000}
              step={50}
              size="xs"
              w={100}
            />
            <Select
              label="Since"
              value={since}
              onChange={(val) => setSince(val || '1h')}
              data={[
                { value: '15m', label: '15 minutes' },
                { value: '30m', label: '30 minutes' },
                { value: '1h', label: '1 hour' },
                { value: '3h', label: '3 hours' },
                { value: '6h', label: '6 hours' },
                { value: '12h', label: '12 hours' },
                { value: '24h', label: '24 hours' },
                { value: '7d', label: '7 days' },
              ]}
              size="xs"
              w={120}
            />
          </Group>

          <Group gap="xs">
            <Tooltip label="Refresh">
              <ActionIcon
                variant="light"
                onClick={() => connectToLogs()}
                loading={isLoading}
              >
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Clear">
              <ActionIcon variant="light" onClick={clearLogs}>
                <IconClearAll size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Download">
              <ActionIcon variant="light" onClick={downloadLogs}>
                <IconDownload size={18} />
              </ActionIcon>
            </Tooltip>
            <CopyButton value={filteredLogs.map(l => l.message).join('\n')}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Copied' : 'Copy all'}>
                  <ActionIcon variant="light" onClick={copy}>
                    {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
        </Group>
      </Paper>

      {/* Status bar */}
      <Group justify="space-between">
        <Group gap="xs">
          <Badge
            color={isConnected ? 'green' : 'red'}
            variant="dot"
            size="sm"
          >
            {isConnected ? 'Connected' : 'Disconnected'}
          </Badge>
          <Text size="xs" c="dimmed">
            {filteredLogs.length} log entries
            {filter && ` (filtered from ${logs.length})`}
          </Text>
        </Group>
        {/* Simple filter input */}
        <Group gap="xs">
          <IconSearch size={14} />
          <input
            type="text"
            placeholder="Filter logs..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              border: '1px solid var(--mantine-color-gray-4)',
              borderRadius: '4px',
              padding: '4px 8px',
              fontSize: '12px',
              width: '200px',
            }}
          />
        </Group>
      </Group>

      {/* Error display */}
      {error && (
        <Paper p="md" withBorder bg="red.0">
          <Group justify="space-between">
            <Text c="red" size="sm">{error}</Text>
            <Button size="xs" variant="light" onClick={() => connectToLogs()}>
              Retry
            </Button>
          </Group>
        </Paper>
      )}

      {/* Loading state */}
      {isLoading && (
        <Paper p="xl" withBorder>
          <Group justify="center">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">Connecting to log stream...</Text>
          </Group>
        </Paper>
      )}

      {/* Logs display */}
      <Paper withBorder style={{ overflow: 'hidden' }}>
        <ScrollArea
          ref={scrollRef}
          h={500}
          type="always"
          className="logs-viewer"
          data-testid="service-logs"
        >
          {filteredLogs.length === 0 && !isLoading ? (
            <Box p="xl" ta="center">
              <Text c="dimmed" size="sm">
                No logs available. Make sure the service has running tasks.
              </Text>
            </Box>
          ) : (
            <Stack gap={0} p="xs">
              {filteredLogs.map((log, index) => (
                <Box
                  key={index}
                  className="log-line log-entry"
                  py={2}
                  px="xs"
                  style={{
                    borderBottom: '1px solid var(--mantine-color-gray-2)',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                  }}
                >
                  <Group gap="xs" wrap="nowrap" align="flex-start">
                    {showTimestamps && log.timestamp && (
                      <Text
                        size="xs"
                        c="dimmed"
                        style={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}
                      >
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </Text>
                    )}
                    {log.taskId && (
                      <Badge
                        size="xs"
                        variant="outline"
                        color="gray"
                        style={{ fontFamily: 'monospace' }}
                      >
                        {log.taskId.slice(0, 8)}
                      </Badge>
                    )}
                    {log.level && log.level !== 'info' && (
                      <Badge size="xs" color={getLevelColor(log.level)}>
                        {log.level.toUpperCase()}
                      </Badge>
                    )}
                    <Code
                      block={false}
                      style={{
                        flex: 1,
                        background: 'transparent',
                        padding: 0,
                        wordBreak: 'break-all',
                      }}
                    >
                      {log.message}
                    </Code>
                  </Group>
                </Box>
              ))}
            </Stack>
          )}
        </ScrollArea>
      </Paper>
    </Stack>
  )
}
